import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AuctionRoomHeader from '../../components/specific/auction/AuctionRoomHeader';
import BidPanel from '../../components/specific/auction/BidPanel';
import BidHistory from '../../components/specific/auction/BidHistory';
import { useAuctionSocket } from '../../hooks/useAuctionSocket';

const MAX_BID_HISTORY_ITEMS = 200;

export default function AuctionRoom() {
    const { sessionId } = useParams();
    const navigate = useNavigate();

    const handleLeaveRoom = () => {
        navigate('/investor/dashboard/bid-sessions');
    };

    const [session, setSession] = useState(null);
    const [highest, setHighest] = useState(null);
    const [bids, setBids] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('loading');

    // --- State for the countdown timer ---
    const [timeLeft, setTimeLeft] = useState(null);

    // --- useEffect for the countdown timer ---
    useEffect(() => {
        // Only run the timer if the session is active and has an end_ts or end_time
        const endTime = session?.end_ts ? (session.end_ts * 1000) : (session?.end_time ? new Date(session.end_time).getTime() : null);

        if (session?.status !== 'active' || !endTime) {
            setTimeLeft(null);
            return;
        }

        const calculateTimeLeft = () => {
            const now = new Date().getTime();
            const difference = endTime - now;

            if (difference > 0) {
                setTimeLeft(difference);
            } else {
                setTimeLeft(0); // Time's up
            }
        };

        // Initial calculation
        calculateTimeLeft();

        // Set up an interval to update every second
        const timerId = setInterval(calculateTimeLeft, 1000);

        // Cleanup function to clear the interval
        return () => clearInterval(timerId);
    }, [session?.end_ts, session?.end_time, session?.status]);

    // Helper function to format time
    const formatTime = (milliseconds) => {
        if (milliseconds === null) return null;
        if (milliseconds <= 0) return '00:00:00';

        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    // Memoize callback functions to prevent unnecessary re-registrations
    // In your AuctionRoom component, modify the callbacks:

    const handleJoined = useCallback((payload) => {
        console.log("✅ Joined Room, loading initial data:", payload);

        if (payload?.history) {
            setBids(Array.isArray(payload.history) ? payload.history.slice(0, MAX_BID_HISTORY_ITEMS) : []);
        }
        if (payload?.highest) {
            setHighest(payload.highest);
        }
        if (payload?.participants) {
            setParticipants(Array.isArray(payload.participants) ? payload.participants : []);
        }
        if (payload?.session) {
            setSession(payload.session);
        }

        setStatus('active');
        setLoading(false);
    }, []);

    const handleBiddingStarted = useCallback((payload) => {
        console.log('[AuctionRoom] bidding_started →', payload);
        // Refresh session to get latest start_time, end_ts, and status=active
        setSession(prev => prev ? { ...prev, status: 'active', ...payload } : null);
    }, []);

    const handleNewHighestBid = useCallback((payload) => {
        console.log("💰 New Highest Bid Received:", payload);
        setHighest({
            amount: payload.amount,
            bidder_id: payload.bidder_id,
            timestamp: payload.timestamp
        });
        // Add to history list immediately
        setBids((prev) => {
            // Prevent duplicates
            if (prev.some(b => b.timestamp === payload.timestamp && b.amount === payload.amount)) {
                return prev;
            }
            return [payload, ...prev].slice(0, MAX_BID_HISTORY_ITEMS);
        });
    }, []);

    const handleBiddingEnded = useCallback((payload) => {
        console.log('[socket] bidding_ended →', payload);
        setSession(s => s ? { ...s, status: 'completed' } : null);
        alert(`Auction ended! Winner: ${payload.winner || 'No one'}`);
    }, []);

    const handleParticipantUpdate = useCallback((data) => {
        console.log("👥 Participant update:", data);
        if (data.client_id) {
            setParticipants((prev) => {
                if (!prev.includes(data.client_id)) return [...prev, data.client_id];
                return prev;
            });
        }
    }, []);

    const handleParticipantLeft = useCallback((data) => {
        console.log("🏃 Participant left:", data);
        if (data.client_id) {
            setParticipants((prev) => prev.filter(id => id !== data.client_id));
        }
    }, []);

    const handleError = useCallback((payload) => {
        alert(payload.message || 'Socket error');
    }, []);

    const { joinBiddingRoom, placeBid, leaveBiddingRoom, isConnected } = useAuctionSocket({
        onJoined: handleJoined,
        onBiddingStarted: handleBiddingStarted,
        onNewHighestBid: handleNewHighestBid,
        onBiddingEnded: handleBiddingEnded,
        onParticipantJoined: handleParticipantUpdate,
        onParticipantLeft: handleParticipantLeft,
        onBidPlaced: (payload) => console.log('[AuctionRoom] bid_placed:', payload),
        onBidRejected: (payload) => alert(payload.message || 'Bid rejected'),
        onError: handleError
    });


    // Join room once and leave on unmount
    useEffect(() => {
        if (isConnected && sessionId) {
            console.log("🔌 Connected, joining room:", sessionId);
            joinBiddingRoom(sessionId);
        }

        return () => {
            if (sessionId) {
                console.log('[AuctionRoom] Leaving room:', sessionId);
                leaveBiddingRoom(sessionId);
            }
        };
    }, [isConnected, sessionId, joinBiddingRoom, leaveBiddingRoom]);

    const handlePlaceBid = (amount) => placeBid(sessionId, amount);

    // Derived readiness state
    const isReady = isConnected && session;

    // Show loading state while fetching session or connecting to socket
    if (loading || !isReady) {
        return (
            <DashboardLayout>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>{loading ? 'Loading Auction Room...' : 'Connecting to auction room...'}</p>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div style={{ padding: '1rem' }}>
                <AuctionRoomHeader
                    highest={highest}
                    session={session}
                    participantCount={participants.length}
                    timeLeft={formatTime(timeLeft)}
                    onLeave={handleLeaveRoom}
                />

                <div className="auction-room-grid">
                    <div className="main-auction-area">
                        <div
                            className="artwork-preview-placeholder"
                            style={{
                                backgroundColor: '#eee',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '8px',
                                marginBottom: '1rem',
                            }}
                        >
                            Artwork Preview Placeholder
                        </div>

                        {session.status === 'active' ? (
                            <BidPanel
                                sessionId={sessionId}
                                currentHighest={highest?.amount || session.starting_price}
                                minIncrement={session.min_bid_increment}
                                onPlaceBid={handlePlaceBid}
                            />
                        ) : (
                            <div
                                style={{
                                    padding: '1rem',
                                    background: '#f8d7da',
                                    color: '#721c24',
                                    borderRadius: '4px',
                                }}
                            >
                                This auction is not active.
                            </div>
                        )}
                    </div>

                    <div className="side-auction-area">
                        <BidHistory bids={bids} />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
